import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/Common/Scrolltotop";
import Home from "./pages/Home";
import axios from "axios";
import { Toaster } from "sonner";
import useAuth from "./services/useAuth.js";

// Home is the landing page, so it ships in the main bundle. Every other page is
// split into its own chunk and downloaded when first visited, which keeps
// admin, booking/Stripe, blog and map code off the home page.
const PackageSearch = lazy(() => import("./pages/PackageSearch"));
const PackageDetail = lazy(() => import("./pages/PackageDetail"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const NotFound = lazy(() => import("./pages/NotFound.jsx"));
const BookingSuccess = lazy(() => import("./components/PackagesPage/Booking/BookingSuccess.jsx"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword.jsx"));
const AuthCallback = lazy(() => import("./pages/AuthCallback.jsx"));
const Blogs = lazy(() => import("./pages/Blogs.jsx"));
const EditTrek = lazy(() => import("./components/AdminDashboard/Trek/EditTrek.jsx"));
const Checkout = lazy(() => import("./components/PackagesPage/Checkout.jsx"));
const TrekBookingForm = lazy(() => import("./components/PackagesPage/Booking/TrekBookingForm.jsx"));
const BookingConfirmation = lazy(() => import("./components/PackagesPage/BookingConfirmation.jsx"));
const BlogDetail = lazy(() => import("./pages/BlogDetails.jsx"));
const Permits = lazy(() => import("./components/Permits/Permits.jsx"));
const ContactUs = lazy(() => import("./pages/ContactUs.jsx"));
const AboutUs = lazy(() => import("./pages/AboutUs.jsx"));

// Shown for the moment a page's code is downloading. Holds the viewport height
// so the footer doesn't flash up the page.
const PageFallback = () => <div style={{ minHeight: "100vh" }} aria-busy="true" />;

axios.defaults.baseURL = `${import.meta.env.VITE_API_URL}/api`;

const App = () => {
  const { user, isLoading } = useAuth();

  return (
    <>
      <ScrollToTop />
      <Toaster richColors className="popp" />
      <Suspense fallback={<PageFallback />}>
      <Routes future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/packages" element={<PackageSearch />} />
        <Route path="/package/:id" element={<PackageDetail />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/blog/:id" element={<BlogDetail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/booking-success" element={<BookingSuccess />} />
        <Route path="/package/edit/:id" element={<EditTrek />} />
        <Route path="*" element={<NotFound />} />
        <Route path="/permits" element={<Permits />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/about-us" element={<AboutUs />} />

        {/* Authentication Routes */}
        <Route path="/signup" element={user ? <Navigate to="/" replace /> : <Signup />} />
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPassword />} />

        {/* Protected Routes */}
        <Route path="/checkout" element={user ? <Checkout /> : <Navigate to="/login" replace />} />
        <Route path="/booking" element={<TrekBookingForm />} />
        <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/login" replace />} />
        <Route path="/booking/confirmation/:bookingId" element={<BookingConfirmation />} />

        {/* Admin Route */}
        <Route path="/admin" element={user?.role === "admin" ? <AdminPage /> : <Navigate to="/login" replace />} />
      </Routes>
      </Suspense>
    </>
  );
};

export default App;